import React, { useState } from 'react';
import { useTodaysVisits } from '../hooks/useTodaysVisits';
import { formatTime } from '../../../shared/utils/dateFormaters';
import { Badge, Spinner } from '../../../shared/components';
import VisitRegistrationModal from './VisitRegistrationModal';
//import './VisitList.css';

const VisitList = () => {
  const { visits, loading, error, refetch } = useTodaysVisits();
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);

  const getStatusBadge = (status) => {
    const statusValue = typeof status === 'object' ? status.value : status;
    const variants = {
      scheduled: 'info',
      checked_in: 'primary',
      in_consultation: 'warning',
      awaiting_lab: 'warning',
      lab_completed: 'info',
      completed: 'success',
      cancelled: 'error',
      no_show: 'error',
    };
    
    const displayStatus = statusValue ? statusValue.replace(/_/g, ' ') : 'unknown';
    return <Badge variant={variants[statusValue] || 'default'}>{displayStatus}</Badge>;
  };

  const handleRegisterClick = () => {
    setShowRegistrationModal(true);
  };

  const handleModalClose = () => {
    setShowRegistrationModal(false);
    refetch(); // Refresh the list after modal closes
  };

  const handleVisitClick = (visit) => {
    setSelectedVisit(visit);
    // You could open a detailed view modal here
  };

  if (loading) return <div className="loading-container"><Spinner /></div>;
  if (error) return <div className="error-container">Error: {error}</div>;
  
  // Ensure visits is an array before using filter methods
  const visitsArray = Array.isArray(visits) ? visits : [];
  
  const scheduledVisits = visitsArray.filter(v => 
    v.visit_status?.value === 'scheduled' || v.visit_status === 'scheduled'
  );
  const completedVisits = visitsArray.filter(v => 
    v.visit_status?.value === 'completed' || v.visit_status === 'completed'
  );
  const inProgressVisits = visitsArray.filter(v => {
    const status = v.visit_status?.value || v.visit_status;
    return ['checked_in', 'in_consultation', 'awaiting_lab', 'lab_completed'].includes(status);
  });

  return (
    <div className="visit-list-container">
      <div className="visit-list-header">
        <h2>Today's Visits</h2>
        <button 
          className="btn-register"
          onClick={handleRegisterClick}
        >
          Register New Visit
        </button>
      </div>

      <div className="visits-summary">
        <div className="summary-card">
          <span className="summary-label">Scheduled</span>
          <span className="summary-value">{scheduledVisits.length}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">In Progress</span>
          <span className="summary-value">{inProgressVisits.length}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Completed</span>
          <span className="summary-value">{completedVisits.length}</span>
        </div>
      </div>

      <div className="visits-sections">
        {inProgressVisits.length > 0 && (
          <div className="visit-section">
            <h3>In Progress</h3>
            <div className="visit-cards">
              {inProgressVisits.map(visit => (
                <div 
                  key={visit.id} 
                  className="visit-card in-progress"
                  onClick={() => handleVisitClick(visit)}
                >
                  <div className="visit-time">{formatTime(visit.visit_date)}</div>
                  <div className="visit-info">
                    <div className="patient-name">{visit.patient?.full_name || 'Unknown'}</div>
                    <div className="patient-id">ID: {visit.patient?.patient_code}</div>
                    <div className="visit-details">
                      <span>Type: {visit.visit_type?.name}</span>
                      {visit.check_in_time && (
                        <span>Checked in: {formatTime(visit.check_in_time)}</span>
                      )}
                    </div>
                  </div>
                  {getStatusBadge(visit.visit_status)}
                </div>
              ))}
            </div>
          </div>
        )}

        {scheduledVisits.length > 0 && (
          <div className="visit-section">
            <h3>Scheduled</h3>
            <div className="visit-cards">
              {scheduledVisits.map(visit => (
                <div 
                  key={visit.id} 
                  className="visit-card scheduled"
                  onClick={() => handleVisitClick(visit)}
                >
                  <div className="visit-time">{formatTime(visit.visit_date)}</div>
                  <div className="visit-info">
                    <div className="patient-name">{visit.patient?.full_name || 'Unknown'}</div>
                    <div className="patient-id">ID: {visit.patient?.patient_code}</div>
                    <div className="visit-details">
                      <span>Type: {visit.visit_type?.name}</span>
                      <span>OHC: {visit.ohc_name}</span>
                    </div>
                  </div>
                  {getStatusBadge(visit.visit_status)}
                </div>
              ))}
            </div>
          </div>
        )}

        {completedVisits.length > 0 && (
          <div className="visit-section">
            <h3>Completed</h3>
            <div className="visit-cards">
              {completedVisits.map(visit => (
                <div 
                  key={visit.id} 
                  className="visit-card completed"
                  onClick={() => handleVisitClick(visit)}
                >
                  <div className="visit-time">{formatTime(visit.visit_date)}</div>
                  <div className="visit-info">
                    <div className="patient-name">{visit.patient?.full_name || 'Unknown'}</div>
                    <div className="patient-id">ID: {visit.patient?.patient_code}</div>
                    <div className="visit-details">
                      <span>Type: {visit.visit_type?.name}</span>
                      <span>Completed: {visit.check_out_time ? formatTime(visit.check_out_time) : 'N/A'}</span>
                    </div>
                  </div>
                  {getStatusBadge(visit.visit_status)}
                </div>
              ))}
            </div>
          </div>
        )}

        {visitsArray.length === 0 && (
          <div className="no-visits">
            <p>No visits scheduled for today</p>
          </div>
        )}
      </div>

      <VisitRegistrationModal 
        isOpen={showRegistrationModal}
        onClose={handleModalClose}
        onSuccess={refetch}
      />
    </div>
  );
};

export default VisitList;